import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorkspaceTab {
  id: string;
  title: string;
  module: string;
  path: string;
  isDirty: boolean;
  closeable: boolean;
  icon?: string;
  params?: Record<string, string>;
  openedAt: number;
}

// ─── State ────────────────────────────────────────────────────────────────────

interface WorkspaceState {
  tabs: WorkspaceTab[];
  activeTabId: string | null;
  recentModules: string[];
}

interface WorkspaceActions {
  openTab: (tab: Omit<WorkspaceTab, 'isDirty' | 'openedAt'>) => void;
  closeTab: (tabId: string) => void;
  closeAllTabs: () => void;
  setActiveTab: (tabId: string) => void;
  markTabDirty: (tabId: string, isDirty: boolean) => void;
  updateTabTitle: (tabId: string, title: string) => void;
  addRecentModule: (module: string) => void;
  getTabById: (tabId: string) => WorkspaceTab | undefined;
}

type WorkspaceStore = WorkspaceState & WorkspaceActions;

const MAX_TABS = 10;
const MAX_RECENT_MODULES = 8;

// ─── Store ────────────────────────────────────────────────────────────────────

export const useWorkspaceStore = create<WorkspaceStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        tabs: [],
        activeTabId: null,
        recentModules: [],

        openTab: (tab) =>
          set((state) => {
            const existing = state.tabs.find((t) => t.id === tab.id);
            if (existing) {
              state.activeTabId = tab.id;
              return;
            }
            // Evict the oldest closeable tab if at capacity
            if (state.tabs.length >= MAX_TABS) {
              const oldest = [...state.tabs]
                .filter((t) => t.closeable && !t.isDirty)
                .sort((a, b) => a.openedAt - b.openedAt)[0];
              if (oldest) {
                state.tabs = state.tabs.filter((t) => t.id !== oldest.id);
              }
            }
            state.tabs.push({ ...tab, isDirty: false, openedAt: Date.now() });
            state.activeTabId = tab.id;
          }),

        closeTab: (tabId) =>
          set((state) => {
            const idx = state.tabs.findIndex((t) => t.id === tabId);
            if (idx === -1) return;
            state.tabs.splice(idx, 1);
            if (state.activeTabId === tabId) {
              // Activate adjacent tab on close
              state.activeTabId = state.tabs[idx - 1]?.id ?? state.tabs[0]?.id ?? null;
            }
          }),

        closeAllTabs: () =>
          set((state) => {
            state.tabs = state.tabs.filter((t) => !t.closeable);
            state.activeTabId = state.tabs[0]?.id ?? null;
          }),

        setActiveTab: (tabId) =>
          set((state) => {
            state.activeTabId = tabId;
          }),

        markTabDirty: (tabId, isDirty) =>
          set((state) => {
            const tab = state.tabs.find((t) => t.id === tabId);
            if (tab) tab.isDirty = isDirty;
          }),

        updateTabTitle: (tabId, title) =>
          set((state) => {
            const tab = state.tabs.find((t) => t.id === tabId);
            if (tab) tab.title = title;
          }),

        addRecentModule: (module) =>
          set((state) => {
            state.recentModules = [
              module,
              ...state.recentModules.filter((m) => m !== module),
            ].slice(0, MAX_RECENT_MODULES);
          }),

        getTabById: (tabId) => get().tabs.find((t) => t.id === tabId),
      })),
      {
        name: 'garagewale-workspace',
        partialize: (state) => ({
          recentModules: state.recentModules,
          // Do not persist tabs — reconstruct from routes on reload
        }),
      },
    ),
    { name: 'WorkspaceStore' },
  ),
);
